import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/customSupabaseClient';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { Users, LayoutDashboard, Settings, LogOut, ShieldAlert } from 'lucide-react';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminUsers from '@/pages/admin/AdminUsers';

const AdminLayout = () => {
    const { session, signOut } = useAuth();
    const [isAdmin, setIsAdmin] = useState(null);
    const [currentTab, setCurrentTab] = useState('dashboard');

    useEffect(() => {
        const checkAdmin = async () => {
            if (!session) {
                window.location.href = '/';
                return;
            }
            try {
                // Call RPC function to check admin rights securely
                const { data, error } = await supabase.rpc('is_admin');
                if (error || !data) {
                    setIsAdmin(false);
                    return;
                }
                setIsAdmin(true);
            } catch (err) {
                console.error("Erro validando admin:", err);
                setIsAdmin(false);
            }
        };
        checkAdmin();
    }, [session]);

    if (isAdmin === null) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900 text-white">
                <ShieldAlert className="w-12 h-12 text-blue-500 mb-4 animate-pulse" />
                <h2 className="text-xl font-bold">Autenticando Acesso Seguro...</h2>
            </div>
        );
    }

    if (isAdmin === false) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900 text-white">
                <ShieldAlert className="w-16 h-16 text-red-500 mb-4" />
                <h2 className="text-3xl font-bold mb-2">Acesso Restrito</h2>
                <p className="text-gray-400 mb-6">Você não tem privilégios de administrador.</p>
                <button 
                    onClick={() => { window.location.href = '/'; }}
                    className="px-6 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors font-medium">
                    Voltar ao Aplicativo
                </button>
            </div>
        );
    }

    const navigation = [
        { id: 'dashboard', name: 'Dashboard', icon: LayoutDashboard },
        { id: 'users', name: 'Usuários', icon: Users },
        { id: 'settings', name: 'Configurações', icon: Settings },
    ];

    const renderContent = () => {
        switch (currentTab) {
            case 'dashboard':
                return <AdminDashboard />;
            case 'users':
                return <AdminUsers />;
            default:
                return <div className="text-slate-500 p-8">Módulo em construção...</div>;
        }
    };

    return (
        <div className="min-h-screen bg-gray-900 text-white flex">
            {/* Sidebar Lateral (Admin) */}
            <aside className="w-64 bg-slate-800 border-r border-slate-700 flex flex-col sticky top-0 h-screen">
                <div className="p-6 border-b border-slate-700">
                    <h1 className="text-xl font-bold text-amber-500 flex items-center gap-2">
                        <ShieldAlert className="w-5 h-5" />
                        Admin Center
                    </h1>
                    <p className="text-xs text-gray-400 mt-1">Super Usuário</p>
                </div>
                
                <nav className="flex-1 p-4 space-y-2">
                    {navigation.map((item) => {
                        const Icon = item.icon;
                        const isActive = currentTab === item.id;
                        return (
                            <button
                                key={item.id}
                                onClick={() => setCurrentTab(item.id)}
                                className={`flex w-full items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                                    isActive 
                                    ? 'bg-amber-500/10 text-amber-500' 
                                    : 'text-gray-400 hover:text-white hover:bg-slate-700'
                                }`}
                            >
                                <Icon className="w-5 h-5" />
                                <span className="font-medium">{item.name}</span>
                            </button>
                        );
                    })}
                </nav>

                <div className="p-4 border-t border-slate-700 w-full flex flex-col">
                    <button
                        onClick={async () => {
                            await signOut();
                            window.location.href = '/';
                        }}
                        className="flex items-center justify-center gap-3 px-4 py-3 w-full text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                    >
                        <LogOut className="w-5 h-5" />
                        <span className="font-medium">Sair do Admin</span>
                    </button>
                    <button
                        onClick={() => { window.location.href = '/'; }}
                        className="mt-2 px-4 py-3 text-sm text-gray-500 hover:text-gray-300 w-full text-center"
                    >
                        Voltar ao App Normal
                    </button>
                </div>
            </aside>

            {/* Conteúdo Principal */}
            <main className="flex-1 overflow-auto bg-gray-900 min-h-screen">
                <div className="p-8">
                    {renderContent()}
                </div>
            </main>
        </div>
    );
};

export default AdminLayout;
